import { useState, useEffect } from "react";
import { apiGet } from "./api";
import type { GatedFeature } from "./gatedFeature";

const ADMIN_TOKEN_STORAGE_KEY = "wallet-app:adminAccessToken";

/**
 * Shared by Trace and Recover: captures a paid session token after a Stripe
 * redirect (?session_id=...), and the personal admin bypass (?admin_token=...,
 * persisted in localStorage under one shared key so setting it on either
 * page unlocks both — matching the token's scope, which grants every
 * GatedFeature, not just the page it was set from).
 *
 * The value bookmarked as admin_token should be a token minted by
 * GET /api/billing/admin/token (itself authenticated with the raw
 * ADMIN_ACCESS_TOKEN secret over Authorization, never a query param) —
 * not the raw secret itself. The minted token is a real, expiring (30
 * day), individually-revocable session token, so this is the only form
 * of the admin credential that should ever end up in a URL, browser
 * history, or localStorage.
 */
export function useAccessToken() {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [tokenPending, setTokenPending] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    let cancelled = false;
    setTokenPending(true);

    async function poll(attemptsLeft: number) {
      if (cancelled) return;
      try {
        const { token } = await apiGet<{ token: string }>(`/api/billing/session/${sessionId}/token`);
        if (!cancelled) {
          setAccessToken(token);
          setTokenPending(false);
        }
        return;
      } catch {
        if (attemptsLeft <= 0) {
          if (!cancelled) setTokenPending(false);
          return;
        }
        setTimeout(() => poll(attemptsLeft - 1), 2000);
      }
    }
    poll(5);

    window.history.replaceState({}, "", window.location.pathname);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const adminToken = params.get("admin_token");
    if (adminToken) {
      try {
        window.localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, adminToken);
      } catch {
        // localStorage unavailable (e.g. private browsing) — still works for this tab via state below.
      }
      setAccessToken(adminToken);
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    try {
      const stored = window.localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
      if (stored) setAccessToken(stored);
    } catch {
      // ignore — just means the bypass won't auto-apply this visit
    }
  }, []);

  return { accessToken, setAccessToken, tokenPending };
}

/**
 * Checks whether `accessToken` actually grants `feature`, via GET
 * /api/billing/entitlements — sends only the token, never recovery data.
 * Needed because unlike Trace's server-enforced routes, Recover has no
 * server chokepoint to reject an under-scoped token at use time: a token
 * that only paid for trace-report-unlock must not silently unlock Recover
 * just because *some* accessToken is present.
 */
export function useEntitlement(accessToken: string | null, feature: GatedFeature) {
  const [entitled, setEntitled] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!accessToken) {
      setEntitled(false);
      return;
    }
    let cancelled = false;
    setChecking(true);
    apiGet<{ features: string[] }>("/api/billing/entitlements", { Authorization: `Bearer ${accessToken}` })
      .then(({ features }) => {
        if (!cancelled) setEntitled(features.includes(feature));
      })
      .catch(() => {
        if (!cancelled) setEntitled(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, feature]);

  return { entitled, checking };
}
